import { z } from 'zod';
import { isUnauthenticated } from './errors';
/** Mirrors the backend's `TokenPair` contract. */
export const TokenPair = z.object({
    accessToken: z.string(),
    /** Opaque and rotating — a new one is issued on every refresh. */
    refreshToken: z.string(),
    tokenType: z.literal('Bearer'),
    /** Access-token lifetime in seconds. */
    expiresIn: z.number().int().positive(),
});
/**
 * Namespaced to THIS app, not to the brand.
 *
 * The back office uses `lb.tokens` and dev-serves on the same
 * `localhost:5173` — same origin, same key, two entirely different identity
 * domains. Running both clobbered each other's sessions, and this app booted
 * holding an admin's token: `platformRole: 'PRO'`, `roles: ['owner']`.
 *
 * Found by opening the page and wondering why a signed-out browser was showing
 * Home. A shared key across two products is not a dev-only annoyance either —
 * it is one deploy decision away from being the same in production.
 */
const STORAGE_KEY = 'lb.web.tokens';
/**
 * The token pair, and the guarantee that only one refresh is ever in flight.
 *
 * Adopted from laboussle-backoffice (§4.3), where the design was already paid
 * for. The two properties worth restating, because both are counter-intuitive:
 *
 * ── Why single-flight matters ─────────────────────────────────────────────
 * The refresh token rotates: every refresh invalidates the one that bought it.
 * A Home screen that fires eight queries on mount gets eight 401s, and eight
 * independent refreshes would race — seven spending a token the first had
 * already consumed, and whichever landed last would leave the store holding a
 * pair the server has since revoked. The member is signed out by their own app.
 *
 * ── Why null does not mean "signed out" ───────────────────────────────────
 * Only a rejection that says the session is *gone* clears the tokens. A 502, a
 * 500 or a phone that lost signal leaves them in place; the original 401
 * surfaces as an ordinary retryable error. Clearing on every failure meant a
 * gateway hiccup logged people out mid-session — worse here than in a back
 * office, since our members are on unreliable connections by definition.
 */
export class AuthStore {
    refresher;
    #tokens = null;
    #refreshing = null;
    #listeners = new Set();
    #persistence = 'local';
    constructor(refresher) {
        this.refresher = refresher;
        const stored = readStored();
        if (stored) {
            this.#tokens = stored.tokens;
            this.#persistence = stored.persistence;
        }
    }
    /** Applies immediately, so signing in with the box unticked moves an existing pair. */
    setPersistence(persistence) {
        this.#persistence = persistence;
        persist(this.#tokens, persistence);
    }
    get tokens() {
        return this.#tokens;
    }
    get accessToken() {
        return this.#tokens?.accessToken ?? null;
    }
    get isAuthenticated() {
        return this.#tokens !== null;
    }
    set(tokens) {
        this.#tokens = tokens;
        persist(tokens, this.#persistence);
        for (const listener of this.#listeners)
            listener(tokens);
    }
    clear() {
        this.set(null);
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }
    /**
     * Refresh, coalescing concurrent callers onto one request.
     *
     * Resolves to null when there is nothing to refresh with or the attempt
     * failed. It does not throw, because every call site is already handling a
     * failure and a second failure mode there only invites a swallowed catch.
     * Callers distinguish "gone" from "broken" by reading `isAuthenticated`.
     */
    async refresh() {
        if (this.#refreshing)
            return this.#refreshing;
        const current = this.#tokens?.refreshToken;
        if (!current)
            return null;
        this.#refreshing = this.refresher(current)
            .then((pair) => {
            this.set(pair);
            return pair;
        })
            .catch((error) => {
            if (isUnauthenticated(error))
                this.clear();
            return null;
        })
            .finally(() => {
            this.#refreshing = null;
        });
        return this.#refreshing;
    }
}
function storageFor(persistence) {
    try {
        // eslint-disable-next-line no-restricted-properties -- the documented owner of this key (§4.3)
        return persistence === 'session' ? window.sessionStorage : window.localStorage;
    }
    catch {
        return null;
    }
}
function readStored() {
    // Session first: if both hold a pair, the tab-scoped one is the more recent
    // decision.
    for (const persistence of ['session', 'local']) {
        try {
            const raw = storageFor(persistence)?.getItem(STORAGE_KEY);
            if (!raw)
                continue;
            const parsed = TokenPair.safeParse(JSON.parse(raw));
            if (parsed.success)
                return { tokens: parsed.data, persistence };
        }
        catch {
            // Try the other one.
        }
    }
    return null;
}
function persist(tokens, persistence) {
    try {
        // Always clear both, so switching the choice cannot leave a stale pair
        // behind in the store we are no longer writing to.
        // eslint-disable-next-line no-restricted-properties -- see above
        window.localStorage.removeItem(STORAGE_KEY);
        // eslint-disable-next-line no-restricted-properties -- see above
        window.sessionStorage.removeItem(STORAGE_KEY);
        if (tokens)
            storageFor(persistence)?.setItem(STORAGE_KEY, JSON.stringify(tokens));
    }
    catch {
        // Persistence is best-effort; the in-memory pair still works for this tab.
    }
}
//# sourceMappingURL=auth-store.js.map